/**
 * Plants-family cards (the block + sensors primitives, UX-003 / UX-004 §4).
 * Health shows as colour+shape+label via HealthBadge/HealthDot; figures via
 * Metric; freshness via FreshnessMark. Unmonitored is never green.
 */
import { useEffect, type ReactNode } from "react";
import type { Figure, Freshness } from "@solarkey/api-client";
import { Metric } from "./metric";
import { FreshnessMark } from "./atoms";
import {
  HealthBadge,
  HealthDot,
  type HealthState,
  type HealthModifier,
} from "./health";

/* ── BlockCard ──────────────────────────────────────────────────────────────
   One block, ranked by deviation from the site median (UX-002 P3). The card
   shows the three key metrics (power, specific yield, deviation), a deviation
   bar, the density-adaptive inverter strip, and a partially-monitored callout
   per the Build-01 wireframe §09. */
export function BlockCard(props: {
  title: string;
  subtitle?: string;
  health: HealthState;
  modifiers?: HealthModifier[];
  metric?: Figure;
  metricLabel?: string;
  specificYield?: Figure;
  deviationPct?: number;
  partiallyMonitored?: number;
  partiallyMonitoredLabel?: string;
  selected?: boolean;
  inverterStrip?: ReactNode;
  onOpen?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className="sk-blockcard"
      onClick={props.onOpen}
      data-interactive={props.onOpen ? "true" : undefined}
      data-selected={props.selected ? "true" : undefined}
      data-health={props.health}
    >
      <div className="sk-blockcard__head">
        <span className="sk-blockcard__title">{props.title}</span>
        <HealthBadge state={props.health} modifiers={props.modifiers} />
      </div>
      {props.subtitle ? (
        <span className="sk-blockcard__subtitle">{props.subtitle}</span>
      ) : null}
      <div className="sk-blockcard__metrics">
        {props.metric ? (
          <Metric figure={props.metric} showFreshness={false} showBasis={false} />
        ) : null}
        {props.specificYield ? (
          <Metric figure={props.specificYield} showFreshness={false} showBasis={false} />
        ) : null}
        {typeof props.deviationPct === "number" ? (
          <span className="sk-blockcard__dev">
            {props.deviationPct > 0 ? "+" : ""}
            {props.deviationPct.toFixed(1)}%
            <span className="sk-blockcard__dev-label">vs median</span>
          </span>
        ) : null}
      </div>
      {typeof props.deviationPct === "number" ? (
        <DeviationBar pct={props.deviationPct} />
      ) : null}
      {props.inverterStrip ?? null}
      {props.partiallyMonitored ? (
        <span className="sk-blockcard__partial">
          {props.partiallyMonitoredLabel ??
            `${props.partiallyMonitored} partially monitored`}
        </span>
      ) : null}
    </button>
  );
}

function DeviationBar(props: { pct: number }): ReactNode {
  const absPct = Math.min(Math.abs(props.pct), 50);
  const barWidth = Math.max((absPct / 50) * 45, 2);
  const barLeft = props.pct < 0 ? 50 - barWidth : 50;
  return (
    <div className="sk-blockcard__devbar" aria-hidden="true">
      <span
        className="sk-blockcard__devbar-fill"
        data-tone={Math.abs(props.pct) > 10 ? "warning" : "neutral"}
        style={{ left: `${barLeft}%`, width: `${barWidth}%` }}
      />
      <span className="sk-blockcard__devbar-mid" />
    </div>
  );
}

/* ── InverterStrip ──────────────────────────────────────────────────────────
   Density-adaptive row of inverters. Visual load scales with what is wrong, not
   with how many exist (UX-003 principle 7): many devices collapse to compact
   cells / bars but each keeps an accessible state label. */
export interface InverterCell {
  id: string;
  health: HealthState;
  label?: string;
}

export function InverterStrip(props: {
  items: InverterCell[];
  density?: "labelled" | "compact" | "bar";
  onSelect?: (id: string) => void;
}): ReactNode {
  const density =
    props.density ??
    (props.items.length <= 16
      ? "labelled"
      : props.items.length <= 64
        ? "compact"
        : "bar");
  return (
    <div
      className="sk-invstrip"
      data-density={density}
      role="group"
      aria-label="Inverters"
    >
      {props.items.map((cell) => {
        const Tag = props.onSelect ? "button" : "span";
        return (
          <Tag
            key={cell.id}
            {...(Tag === "button" ? { type: "button" as const } : { role: "img" as const })}
            className="sk-invstrip__cell"
            data-state={cell.health}
            aria-label={`${cell.label ?? cell.id}: ${cell.health}`}
            title={`${cell.label ?? cell.id} · ${cell.health}`}
            onClick={props.onSelect ? () => props.onSelect?.(cell.id) : undefined}
          >
            {density === "labelled" ? (
              <span className="sk-invstrip__label">{cell.label ?? cell.id}</span>
            ) : null}
          </Tag>
        );
      })}
    </div>
  );
}

/* ── EquipmentDrawer ────────────────────────────────────────────────────────
   One drawer at a time (UX-003 §7). Presentational shell; screens compose the
   nameplate, history, findings and actions inside. Esc / overlay closes it. */
export function EquipmentDrawer(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  health?: HealthState;
  modifiers?: HealthModifier[];
  children?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  const { open, onClose } = props;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="sk-drawer" role="presentation">
      <div className="sk-drawer__scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="sk-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="sk-drawer__head">
          <span className="sk-drawer__title">{props.title}</span>
          {props.health ? (
            <HealthBadge state={props.health} modifiers={props.modifiers} />
          ) : null}
          <button
            type="button"
            className="sk-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="sk-drawer__body">{props.children}</div>
        {props.actions ? <footer className="sk-drawer__foot">{props.actions}</footer> : null}
      </div>
    </div>
  );
}

/* ── HistoryCard ────────────────────────────────────────────────────────────
   The 30-day history: faults, performance, maintenance, replacements, warranty
   (UX-002 P7). */
export interface HistoryEntry {
  id: string;
  kind: string;
  when: string;
  label: string;
}

export function HistoryCard(props: {
  title?: string;
  entries: HistoryEntry[];
  emptyState?: ReactNode;
}): ReactNode {
  return (
    <section className="sk-history">
      {props.title ? <h3 className="sk-history__title">{props.title}</h3> : null}
      {props.entries.length === 0 ? (
        <div className="sk-history__empty">{props.emptyState ?? null}</div>
      ) : (
        <ul className="sk-history__list" role="list">
          {props.entries.map((e) => (
            <li className="sk-history__row" key={e.id}>
              <span className="sk-history__kind">{e.kind}</span>
              <span className="sk-history__label">{e.label}</span>
              <span className="sk-history__when">{e.when}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── DataPathStrip ──────────────────────────────────────────────────────────
   The per-hop source chain with freshness (UX-003 §3.3, UX-005 §4.7). When the
   path is down, the whole strip is greyed with ages — one fault, one statement,
   never many device alarms. */
export interface DataPathHop {
  id: string;
  sourceKind: string;
  label?: string;
  freshness?: Freshness;
}

const SOURCE_KIND_LABEL: Record<string, string> = {
  device: "Device",
  logger: "Logger",
  edge_node: "Edge node",
  site_scada: "Site SCADA",
  cloud_ingest: "Cloud ingest",
  engines: "Engines",
  platform: "Platform",
};

export function DataPathStrip(props: {
  hops: DataPathHop[];
  pathDown?: boolean;
}): ReactNode {
  return (
    <ol className="sk-datapath" data-down={props.pathDown ? "true" : undefined}>
      {props.hops.map((hop, i) => (
        <li className="sk-datapath__hop" key={hop.id}>
          {i > 0 ? <span className="sk-datapath__arrow" aria-hidden="true">→</span> : null}
          {/* A hop with no freshness has NOT been checked — it must never default
              to "live". Claiming a healthy data path we did not verify is exactly
              the fabrication the ruling forbids. */}
          <span className="sk-datapath__box" data-state={hop.freshness?.state ?? "unknown"}>
            <span className="sk-datapath__kind">
              {hop.label ?? SOURCE_KIND_LABEL[hop.sourceKind] ?? hop.sourceKind}
            </span>
            <FreshnessMark freshness={hop.freshness} />
          </span>
        </li>
      ))}
    </ol>
  );
}

// Re-export the dense dot for consumers composing their own strips.
export { HealthDot };
